// RippleOne Theme Provider
// Provides design system context throughout the app

import React, { createContext, useContext, ReactNode } from 'react';
import { DESIGN_TOKENS, COMPONENTS, LAYOUT, THEME, MOTION, utils } from './index';

// ========================================
// THEME CONTEXT
// ========================================

interface ThemeContextType {
  tokens: typeof DESIGN_TOKENS;
  components: typeof COMPONENTS;
  layout: typeof LAYOUT;
  theme: typeof THEME;
  motion: typeof MOTION;
  utils: typeof utils;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ========================================
// THEME PROVIDER
// ========================================

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const value = {
    tokens: DESIGN_TOKENS,
    components: COMPONENTS,
    layout: LAYOUT,
    theme: THEME,
    motion: MOTION,
    utils
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// ========================================
// THEME HOOK
// ========================================

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ========================================
// DESIGN TOKEN HOOKS
// ========================================

// Specific hooks for common use cases
export const useDesignTokens = () => {
  const { tokens } = useTheme();
  return tokens;
};

export const useComponents = () => {
  const { components } = useTheme();
  return components;
};

export const useLayout = () => {
  const { layout } = useTheme();
  return layout;
};

export const useMotion = () => {
  const { motion } = useTheme();
  return motion;
};

export const useUtils = () => {
  const { utils } = useTheme();
  return utils;
};

// ========================================
// TYPED STYLE HELPERS
// ========================================

// Helper hooks that provide type-safe access to design tokens
export const useTypography = () => {
  const { tokens } = useTheme();
  return {
    compact: tokens.typography.compact,
    data: tokens.typography.data,
    headings: tokens.typography.headings,
    body: tokens.typography.body,
    badge: tokens.typography.badge,
    caption: tokens.typography.caption,
    label: tokens.typography.label,
    code: tokens.typography.code
  };
};

export const useSpacing = () => {
  const { tokens } = useTheme();
  return {
    spacing: tokens.spacing,
    padding: tokens.padding,
    container: tokens.container
  };
};

export const useColors = () => {
  const { tokens } = useTheme();
  return tokens.colors;
};

export const useAnimations = () => {
  const { tokens } = useTheme();
  return tokens.animations;
};

// ========================================
// COMPONENT STYLE HELPERS
// ========================================

export const useButtonStyles = () => {
  const { components, utils } = useTheme();
  
  return {
    getButton: (variant: keyof typeof components.buttons, size: keyof typeof components.buttonSizes = 'md') => {
      return utils.cn(components.buttons[variant], components.buttonSizes[size]);
    },
    variants: components.buttons,
    sizes: components.buttonSizes
  };
};

export const useCardStyles = () => {
  const { components } = useTheme();
  return components.cards;
};

export const useBadgeStyles = () => {
  const { components } = useTheme();
  return components.badges;
};

export const useInputStyles = () => {
  const { components } = useTheme();
  return components.inputs;
};

export const useIconStyles = () => {
  const { components } = useTheme();
  return components.icons;
};

// ========================================
// LAYOUT HELPERS
// ========================================

export const useLayoutHelpers = () => {
  const { layout, utils } = useTheme();
  
  return {
    getContainer: (size: keyof typeof layout.page = 'container') => layout.page[size],
    getGrid: (columns: keyof typeof layout.page.grid) => layout.page.grid[columns],
    getFlex: (type: keyof typeof layout.flex) => layout.flex[type],
    section: layout.page.section
  };
};

// ========================================
// THEME UTILITIES
// ========================================

export const useThemeUtils = () => {
  const { utils, theme } = useTheme();
  
  return {
    cn: utils.cn,
    getResponsivePadding: utils.getResponsivePadding,
    getContainer: utils.getContainer,
    getButton: utils.getButton,
    backgrounds: theme.backgrounds,
    effects: theme.effects,
    glass: theme.glass
  };
};

// ========================================
// RESPONSIVE HELPERS
// ========================================

export const useResponsive = () => {
  const { tokens } = useTheme();
  
  return {
    // Get responsive padding based on screen size
    getPadding: (size: keyof typeof tokens.padding = 'md') => tokens.padding[size],
    
    // Get responsive container based on content width needed
    getContainer: (size: keyof typeof tokens.container = 'xl') => tokens.container[size],
    
    // Get responsive typography
    getCompactText: (size: keyof typeof tokens.typography.compact = 'base') => tokens.typography.compact[size],
    getDataText: (size: keyof typeof tokens.typography.data = 'base') => tokens.typography.data[size],
    getHeading: (level: keyof typeof tokens.typography.headings = 'h2') => tokens.typography.headings[level],
    getBody: (size: keyof typeof tokens.typography.body = 'sm') => tokens.typography.body[size]
  };
};

// ========================================
// ANIMATION HELPERS
// ========================================

export const useAnimationHelpers = () => {
  const { motion, tokens } = useTheme();
  
  return {
    // Framer Motion presets
    fadeInUp: motion.fadeInUp,
    fadeInScale: motion.fadeInScale,
    slideInLeft: motion.slideInLeft,
    hover: motion.hover,
    stagger: motion.stagger,
    
    // CSS animation classes
    timing: tokens.animations.timing,
    easing: tokens.animations.easing,
    transforms: tokens.animations.transforms
  };
};

// ========================================
// EXPORTS
// ========================================

export default ThemeProvider; 